// ---------------------------------------------------------------------------
// estimatorQueue — durable, offline-tolerant write queue for the ESTIMATOR
// app (WalkthroughPortal). Sibling of portalQueue.js (crew portal), same
// IndexedDB pattern, different transport: items here replay SUPABASE db
// calls (the estimator app writes through supabase-js as a staff session,
// not through a serverless endpoint).
//
// Why it exists (Jaylen 2026-07-02): estimators work in houses with bad
// cell signal. A Details save that fails on the spot must not lose the
// data — it parks here and syncs when signal returns.
//
// Scope: client PATCHes (the Details tab save and the walkthrough-status
// stamps) and, since 2026-09-18, move upserts (trip-strip date taps).
// Notes use draft-preservation instead (see ClientNotesPanel); photos
// stay online-only with per-photo retry.
//
// Dedupe: put() replaces same-key items, so repeated saves of the same
// client's details collapse to ONE pending item carrying the latest data.
// Keys: `details:<clientId>` / `wtstatus:<clientId>`.
//
// Degrades gracefully: no IndexedDB (private mode) → plain direct call,
// i.e. exactly the old behavior, never worse.
// ---------------------------------------------------------------------------

import db from './database';

const DB_NAME = 'bbm_estimator';
const STORE = 'write_queue';
const DB_VERSION = 1;
const MAX_ATTEMPTS = 10;

let _flushing = false;
let _initialized = false;
let _snapshot = [];
const _listeners = new Set();

// ── Executors: how each queued kind is replayed ─────────────────────────
// patch_client: { id, patch } → targeted UPDATE via db.patchClient (never
// the upsert path — the clients.name NOT NULL footgun).
async function execute(item) {
    if (item.kind === 'patch_client') {
        await db.patchClient(item.args.id, item.args.patch);
        return;
    }
    // upsert_move (2026-09-18, moves): { id, ...fields } → db.upsertMove.
    // Lets an offline tap on the trip strip park (unload date / window,
    // tentative flags) instead of being dropped; the DB mirror fans the
    // move out to its options + the client when it replays.
    if (item.kind === 'upsert_move') {
        await db.upsertMove(item.args);
        return;
    }
    throw new Error(`Unknown queue kind: ${item.kind}`);
}

// Classify a thrown error: network-level failures mean "offline, retry
// later"; anything else is a real rejection that retrying won't fix.
// Exported so callers can do try-direct-then-enqueue (keeps the store's
// local state fresh on the happy path).
export function isNetworkError(e) {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    const msg = String(e?.message || e || '').toLowerCase();
    return msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed') || msg.includes('timeout');
}

// ── IndexedDB plumbing (mirrors portalQueue) ────────────────────────────
function idbAvailable() {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const d = req.result;
            if (!d.objectStoreNames.contains(STORE)) {
                d.createObjectStore(STORE, { keyPath: 'key' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

function reqP(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

async function getAllItems() {
    if (!idbAvailable()) return [];
    const d = await openDB();
    try { return (await reqP(d.transaction(STORE).objectStore(STORE).getAll())) || []; }
    finally { d.close(); }
}

async function putItem(item) {
    const d = await openDB();
    try { await reqP(d.transaction(STORE, 'readwrite').objectStore(STORE).put(item)); }
    finally { d.close(); }
}

async function deleteItem(key) {
    const d = await openDB();
    try { await reqP(d.transaction(STORE, 'readwrite').objectStore(STORE).delete(key)); }
    finally { d.close(); }
}

// ── Snapshot + subscription (drives the header sync chip) ───────────────
async function refreshSnapshot() {
    try { _snapshot = await getAllItems(); } catch { /* keep last */ }
    for (const fn of _listeners) {
        try { fn(_snapshot); } catch { /* ignore */ }
    }
}

export function getSnapshot() { return _snapshot; }

export function subscribe(fn) {
    _listeners.add(fn);
    fn(_snapshot);
    return () => _listeners.delete(fn);
}

export function queueStats(snapshot = _snapshot) {
    let pending = 0, failed = 0;
    for (const it of snapshot) {
        if (it.status === 'failed') failed++;
        else pending++;
    }
    return { pending, failed, total: snapshot.length };
}

function isOffline() {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ── Core: attempt one item ──────────────────────────────────────────────
// Returns 'synced' | 'offline' | 'failed'
async function _syncOne(item) {
    try {
        await putItem({ ...item, status: 'syncing', updatedAt: Date.now() });
        await refreshSnapshot();
    } catch { /* proceed anyway */ }

    try {
        await execute(item);
        try { await deleteItem(item.key); } catch { /* ignore */ }
        await refreshSnapshot();
        return { outcome: 'synced' };
    } catch (e) {
        if (isNetworkError(e)) {
            await safePut({ ...item, status: 'pending', attempts: item.attempts + 1, lastError: 'offline', updatedAt: Date.now() });
            return { outcome: 'offline' };
        }
        await safePut({ ...item, status: 'failed', attempts: item.attempts + 1, lastError: e?.message || 'error', updatedAt: Date.now() });
        return { outcome: 'failed', error: e?.message };
    }
}

async function safePut(item) {
    try { await putItem(item); await refreshSnapshot(); } catch { /* ignore */ }
}

// ── Public: enqueue + immediate attempt ─────────────────────────────────
// { key, kind, args, label } → outcome as above. Callers treat 'offline'
// as success-with-caveat ("saved on this phone, will sync").
export async function enqueueAndSync({ key, kind, args, label }) {
    const now = Date.now();
    const item = { key, kind, args, label: label || kind, status: 'pending', attempts: 0, lastError: null, createdAt: now, updatedAt: now };

    if (!idbAvailable()) {
        // No durable store — direct call, today's behavior.
        try { await execute(item); return { outcome: 'synced' }; }
        catch (e) { return { outcome: isNetworkError(e) ? 'offline' : 'failed', error: e?.message }; }
    }

    try {
        await putItem(item); // same-key replace = dedupe
        await refreshSnapshot();
    } catch {
        try { await execute(item); return { outcome: 'synced' }; }
        catch (e) { return { outcome: isNetworkError(e) ? 'offline' : 'failed', error: e?.message }; }
    }

    if (isOffline()) return { outcome: 'offline' };
    return _syncOne(item);
}

// ── Flush (auto-triggered on online/focus/visibility/interval) ──────────
export async function flush() {
    if (_flushing || isOffline() || !idbAvailable()) return;
    _flushing = true;
    try {
        const items = (await getAllItems()).sort((a, b) => a.createdAt - b.createdAt);
        for (const item of items) {
            if (item.status === 'failed' && item.attempts >= MAX_ATTEMPTS) continue;
            const r = await _syncOne(item);
            if (r.outcome === 'offline') break; // lost signal — stop for now
        }
    } catch { /* next trigger retries */ }
    finally { _flushing = false; }
}

export async function retryFailed() {
    if (!idbAvailable()) return;
    try {
        const items = await getAllItems();
        for (const it of items) {
            if (it.status === 'failed') await safePut({ ...it, status: 'pending', attempts: 0, lastError: null });
        }
    } catch { /* ignore */ }
    flush();
}

// ── Init — call once on estimator portal mount ──────────────────────────
export function initEstimatorSync() {
    refreshSnapshot();
    if (_initialized) { flush(); return; }
    _initialized = true;
    if (typeof window !== 'undefined') {
        window.addEventListener('online', () => flush());
        window.addEventListener('focus', () => flush());
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => { if (!document.hidden) flush(); });
        }
        setInterval(() => { if (_snapshot.length) flush(); }, 30000);
    }
    flush();
}
